import { Icon } from '@iconify/react'
import { Box, Collapse, IconButton, Tab, Tabs, Typography, useTheme } from '@mui/material'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import KanbanItem from './KanbanItem'
import { Kanban, Material } from './types'

// ─── Types untuk mode withTabs ──────────────────────────────────────────────

export interface TabKanbanRecommendation {
  id: string
  code: string
  actualQty: number
  recTaken: number
}

export interface TabKanbanActual {
  code: string
  name: string
  actualQty: number
  taken: number
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface MaterialItemProps extends Material {
  onQuantityAdjust?: (kanban: Kanban) => void
  onScanKanban?: (kanban: Kanban) => void
  actionPosition?: 'left' | 'right'
  viewOnly?: boolean
  hideCode?: boolean

  // Props baru: aktif hanya ketika withTabs=true (DO_READY_TO_SHOPPING / PO_SHOPPING)
  withTabs?: boolean
  tabRecommendationKanbans?: TabKanbanRecommendation[]
  tabActualKanbans?: TabKanbanActual[]
  onScanRecommendationKanban?: (item: TabKanbanRecommendation) => void
  onAdjustActualTaken?: (entry: TabKanbanActual) => void
  onDeleteActualKanban?: (kanbanCode: string) => void
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Card untuk tab Recommendation — Actual Qty & Rec.Taken (read-only), swipe kiri untuk scan */
const RecommendationCard = memo<{
  item: TabKanbanRecommendation
  onScan: () => void
  isScanned?: boolean
}>(({ item, onScan, isScanned = false }) => {
  const theme = useTheme()

  const REVEAL_WIDTH = 42
  const SIDEBAR_MIN = 18
  const THRESHOLD = 24
  const [translateX, setTranslateX] = useState(0)
  const [dragging, setDragging] = useState(false)
  const startXRef = useRef(0)

  const onTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX
    setDragging(true)
  }
  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return
    const delta = startXRef.current - e.touches[0].clientX
    setTranslateX(delta <= 0 ? 0 : Math.min(REVEAL_WIDTH, delta))
  }
  const onTouchEnd = () => {
    setDragging(false)
    const final = translateX > THRESHOLD ? REVEAL_WIDTH : 0
    setTranslateX(final)
    if (final === REVEAL_WIDTH) {
      onScan()
      setTranslateX(0)
    }
  }

  const panelWidth = Math.max(SIDEBAR_MIN, Math.min(REVEAL_WIDTH, translateX))

  return (
    <Box
      sx={{
        position: 'relative',
        background: theme.colorToken.background.neutral.subtlest,
        borderRadius: 2,
        border: `1px solid ${theme.palette.neutral?.[50] || '#e0e0e0'}`,
        mb: 2,
        overflow: 'hidden',
        px: 1,
        py: 2
      }}
    >
      {/* Action panel — identik dengan KanbanItem */}
      <Box
        onClick={() => {
          if (!isScanned) onScan()
        }}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: panelWidth,
          background: isScanned
            ? theme.colorToken.background.success.normal
            : theme.colorToken.background.danger.normal,
          color: theme.colorToken.text.neutralInverted.normal,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
          userSelect: 'none',
          cursor: isScanned ? 'default' : 'pointer',
          zIndex: 0
        }}
      >
        {panelWidth <= SIDEBAR_MIN + 6 ? (
          <Icon icon={isScanned ? 'tabler:checks' : 'tabler:chevrons-left'} color='white' fontSize={12} />
        ) : (
          <>
            <Icon icon='tabler:scan' color='white' fontSize={14} />
            <Typography color='white' variant='caption'>
              Scan
            </Typography>
          </>
        )}
      </Box>

      {/* Content layer — swipeable */}
      <Box
        onTouchStart={isScanned ? undefined : onTouchStart}
        onTouchMove={isScanned ? undefined : onTouchMove}
        onTouchEnd={isScanned ? undefined : onTouchEnd}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pl: 1.5,
          pr: 7,
          py: 1.5,
          position: 'relative',
          zIndex: 1,
          touchAction: 'pan-y',
          transform: `translateX(-${translateX - 17}px)`,
          transition: dragging ? 'none' : 'transform 220ms ease-out'
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='body2' fontWeight={500}>
            {item.code}
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              height: 16,
              px: 1,
              borderRadius: 1,
              alignItems: 'center',
              fontSize: 12,
              backgroundColor: isScanned
                ? theme.colorToken.background.success.subtlest
                : theme.colorToken.background.danger.subtlest,
              color: isScanned ? theme.colorToken.text.success.normal : theme.colorToken.text.danger.normal,
              alignSelf: 'flex-start'
            }}
          >
            {isScanned ? 'Scanned' : 'Unscanned'}
          </Box>
        </Box>

        {/* Actual Qty */}
        <QtyBox label='Actual Qty' value={item.actualQty} />
        {/* Rec.Taken */}
        <QtyBox label='Rec.Taken' value={item.recTaken} />
      </Box>
    </Box>
  )
})

/** Card untuk tab Actual Shopping — Actual Qty + Taken (clickable) + delete */
const ActualCard = memo<{
  entry: TabKanbanActual
  onAdjust: () => void
  onDelete: () => void
}>(({ entry, onAdjust, onDelete }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        background: theme.colorToken.background.neutral.subtlest,
        borderRadius: 2,
        border: `1px solid ${theme.palette.neutral?.[50] || '#e0e0e0'}`,
        mb: 2,
        px: 1.5,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant='body2' fontWeight={500}>
          {entry.code}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            height: 16,
            px: 1,
            borderRadius: 1,
            alignItems: 'center',
            fontSize: 12,
            backgroundColor: theme.colorToken.background.success.subtlest,
            color: theme.colorToken.text.success.normal,
            alignSelf: 'flex-start'
          }}
        >
          Scanned
        </Box>
      </Box>

      {/* Actual Qty */}
      <QtyBox label='Actual Qty' value={entry.actualQty} />

      {/* Taken — clickable */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10, whiteSpace: 'nowrap' }}>
          Taken
        </Typography>
        <Box
          onClick={onAdjust}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.colorToken.background.success.subtlest,
            color: theme.colorToken.text.success.normal,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': {
              background: theme.colorToken.background.success.normal,
              color: theme.colorToken.text.neutralInverted.normal
            }
          }}
        >
          {entry.taken}
        </Box>
      </Box>

      {/* Delete */}
      <IconButton size='small' onClick={onDelete} sx={{ color: theme.palette.error.main }}>
        <Icon icon='tabler:x' fontSize={16} />
      </IconButton>
    </Box>
  )
})

/** Reusable qty display box */
const QtyBox = memo<{ label: string; value: number }>(({ label, value }) => {
  const theme = useTheme()
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.colorToken.background.primary.subtlest,
          color: theme.colorToken.text.primary.normal,
          fontSize: 12,
          fontWeight: 500
        }}
      >
        {value}
      </Box>
    </Box>
  )
})

// ─── Main Component ───────────────────────────────────────────────────────────

const MaterialItem = memo<MaterialItemProps>(
  ({
    name,
    code,
    rack,
    scannedCount,
    kanbanCount,
    orderQty,
    total,
    kanbans,
    onQuantityAdjust,
    onScanKanban,
    actionPosition = 'right',
    viewOnly = false,
    hideCode = false,
    // withTabs props
    withTabs = false,
    tabRecommendationKanbans = [],
    tabActualKanbans = [],
    onScanRecommendationKanban,
    onAdjustActualTaken,
    onDeleteActualKanban
  }) => {
    // Set kode kanban yang sudah di-scan (ada di actual shopping)
    const scannedCodesSet = useMemo(() => new Set(tabActualKanbans.map(e => e.code)), [tabActualKanbans])
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)
    const [activeTab, setActiveTab] = useState(0)
    const canExpand = (kanbanCount || 0) > 0

    useEffect(() => {
      if (viewOnly) setExpanded(true)
    }, [viewOnly])

    // Ketika withTabs aktif, expand otomatis
    useEffect(() => {
      if (withTabs) setExpanded(true)
    }, [withTabs])

    const status = useMemo(() => {
      const kanbanMismatch = kanbans.some(k => k.hasQuantityMismatch)
      const materialMismatch = (scannedCount === kanbanCount && total !== orderQty) || total > orderQty
      const hasScanned = scannedCount > 0

      if (kanbanMismatch || materialMismatch) return 'warning'
      if (hasScanned) return 'success'
      return 'danger'
    }, [kanbans, scannedCount, kanbanCount, total, orderQty])

    // Ketika withTabs aktif, status ditentukan dari tabActualKanbans count
    const tabStatus = useMemo(() => {
      if (!withTabs) return status
      return tabActualKanbans.length > 0 ? 'success' : 'danger'
    }, [withTabs, tabActualKanbans.length, status])

    const statusColors = theme.colorToken.background[withTabs ? tabStatus : status]
    const borderColor = viewOnly
      ? theme.palette.neutral[50]
      : expanded
        ? statusColors.normal
        : theme.palette.neutral[50]

    const actualCount = tabActualKanbans.length

    return (
      <Box
        sx={{
          background: 'white',
          borderRadius: 1.5,
          border: `1px solid ${borderColor}`,
          mb: 1.5,
          overflow: 'hidden'
        }}
      >
        {/* ── Header ── */}
        <Box
          onClick={() => {
            if (!canExpand && !withTabs) return
            setExpanded(!expanded)
          }}
          sx={{
            p: 1.5,
            pr: 3,
            py: 4,
            background: viewOnly ? 'white' : expanded ? statusColors.subtlest : 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: canExpand || withTabs ? 'pointer' : 'default'
          }}
        >
          <IconButton size='medium' disabled={!canExpand && !withTabs}>
            <Icon
              icon={expanded ? 'tabler:chevron-down' : 'tabler:chevron-right'}
              color={canExpand || withTabs ? theme.colorToken.icon.neutral.boldest : theme.palette.action.disabled}
            />
          </IconButton>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant='body2' fontWeight={500}>
              {name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='caption' color='text.secondary'>
                {code}
              </Typography>
              {!viewOnly && (
                <Box
                  sx={{
                    px: 0.5,
                    py: 0.25,
                    background: statusColors.subtlest,
                    color: theme.colorToken.text[withTabs ? tabStatus : status].normal,
                    borderRadius: 0.5,
                    fontSize: 12
                  }}
                >
                  {withTabs ? `${actualCount} Scanned` : `${scannedCount} Scanned`}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              {(rack || '').trim() !== '-' && (rack || '').trim() !== '' && (
                <>
                  <Icon icon='tabler:server' fontSize={14} />
                  <Typography variant='caption' mr={1}>
                    {rack}
                  </Typography>
                </>
              )}
              <Icon icon='tabler:squares-filled' fontSize={14} />
              <Typography variant='caption'>{kanbanCount} Kanban</Typography>
            </Box>
          </Box>

          <Box
            sx={{
              width: 36,
              height: 36,
              background: theme.colorToken.background.primary.subtlest,
              color: theme.colorToken.text.primary.normal,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 500
            }}
          >
            {parseFloat(Number(orderQty).toFixed(4))}
          </Box>
        </Box>

        {/* ── Collapse: mode withTabs ── */}
        {withTabs && (
          <Collapse in={expanded}>
            <Box sx={{ borderTop: `1px solid ${theme.palette.neutral?.[50] || '#e0e0e0'}` }}>
              {/* Tabs header */}
              <Tabs
                value={activeTab}
                onChange={(_, val) => setActiveTab(val)}
                sx={{
                  px: 2,
                  minHeight: 36,
                  borderBottom: `1px solid ${theme.palette.neutral?.[50] || '#e0e0e0'}`,
                  '& .MuiTab-root': { minHeight: 36, py: 0, fontSize: 13 }
                }}
              >
                <Tab label='Recomendation' />
                <Tab
                  label={
                    actualCount > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            background: theme.colorToken.background.success.normal,
                            color: 'white',
                            fontSize: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600
                          }}
                        >
                          {actualCount}
                        </Box>
                        Actual Shopping
                      </Box>
                    ) : (
                      'Actual Shopping'
                    )
                  }
                />
              </Tabs>

              {/* Tab panels */}
              <Box sx={{ p: 3, pb: 1 }}>
                {activeTab === 0 && (
                  <>
                    {tabRecommendationKanbans.length === 0 ? (
                      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant='subtitle2' fontWeight={500}>
                          No Data Show
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          No recommendation kanban available.
                        </Typography>
                      </Box>
                    ) : (
                      tabRecommendationKanbans.map(item => (
                        <RecommendationCard
                          key={item.id}
                          item={item}
                          isScanned={scannedCodesSet.has(item.code)}
                          onScan={() => onScanRecommendationKanban?.(item)}
                        />
                      ))
                    )}
                  </>
                )}

                {activeTab === 1 && (
                  <>
                    {tabActualKanbans.length === 0 ? (
                      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant='subtitle2' fontWeight={500}>
                          No Data Show
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Please scan kanban to show data.
                        </Typography>
                      </Box>
                    ) : (
                      tabActualKanbans.map(entry => (
                        <ActualCard
                          key={entry.code}
                          entry={entry}
                          onAdjust={() => onAdjustActualTaken?.(entry)}
                          onDelete={() => onDeleteActualKanban?.(entry.code)}
                        />
                      ))
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Collapse>
        )}

        {/* ── Collapse: mode normal (existing) ── */}
        {!withTabs && (
          <Collapse in={expanded && canExpand}>
            <Box sx={{ p: 3, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {kanbans.map((kanban, index) => (
                <KanbanItem
                  key={`${kanban.name}-${index}`}
                  {...kanban}
                  actionPosition={actionPosition}
                  onQuantityAdjust={onQuantityAdjust}
                  onScanKanban={onScanKanban}
                  disableSwipe={viewOnly}
                  viewOnly={viewOnly}
                  hideCode={hideCode}
                />
              ))}
            </Box>
          </Collapse>
        )}
      </Box>
    )
  }
)

export default MaterialItem
